import { useCallback, useMemo } from 'react';

export const LEFT_DOTS = 'LEFT_DOTS';
export const RIGHT_DOTS = 'RIGHT_DOTS';

const SIBLING_COUNT = 1;
const DOTS_JUMP = 5;

/**
 * Builds a fixed-size pagination range around the active page.
 * Always returns at most 7 items — page indices, LEFT_DOTS, or RIGHT_DOTS.
 *
 * The output falls into one of four cases:
 *
 * 1. Few pages (≤ 7)         → [0, 1, 2, 3, 4, 5, 6]
 * 2. Near the start (page 1) → [0, 1, 2, 3, 4, '...', 999]
 * 3. In the middle (page 50) → [0, '...', 49, 50, 51, '...', 999]
 * 4. Near the end (page 998) → [0, '...', 995, 996, 997, 998, 999]
 *
 * Clicking '...' jumps 5 pages in that direction (handled by usePagination).
 *
 * @param {object} params
 * @param {number} params.totalPages - Total number of pages
 * @param {number} params.activePage - Current active page (0-indexed)
 * @returns {Array<number|string>} Page indices and dot placeholders
 */
const generatePaginationRange = ({ totalPages, activePage }) => {
	if (totalPages <= 0) return [];

	const totalPageNumbers = SIBLING_COUNT * 2 + 5;

	if (totalPages <= totalPageNumbers) {
		return Array.from({ length: totalPages }, (_, i) => i);
	}

	const leftSiblingIndex = Math.max(activePage - SIBLING_COUNT, 0);
	const rightSiblingIndex = Math.min(activePage + SIBLING_COUNT, totalPages - 1);
	const showLeftDots = leftSiblingIndex > 2;
	const showRightDots = rightSiblingIndex < totalPages - 3;
	const lastPageIndex = totalPages - 1;

	if (!showLeftDots && showRightDots) {
		const leftItemCount = 3 + 2 * SIBLING_COUNT;
		const leftRange = Array.from({ length: leftItemCount }, (_, i) => i);

		return [...leftRange, RIGHT_DOTS, lastPageIndex];
	}

	if (showLeftDots && !showRightDots) {
		const rightItemCount = 3 + 2 * SIBLING_COUNT;
		const rightRange = Array.from(
			{ length: rightItemCount },
			(_, i) => totalPages - rightItemCount + i
		);

		return [0, LEFT_DOTS, ...rightRange];
	}

	const middleRange = Array.from(
		{ length: rightSiblingIndex - leftSiblingIndex + 1 },
		(_, i) => leftSiblingIndex + i
	);

	return [0, LEFT_DOTS, ...middleRange, RIGHT_DOTS, lastPageIndex];
};

export const usePagination = ({ activePage, setActivePage, pageCount }) => {
	const totalPages = pageCount?.totalPages ?? 0;
	const totalCount = pageCount?.totalCount ?? 0;
	const size = pageCount?.size ?? 0;

	const paginationRange = useMemo(
		() => generatePaginationRange({ totalPages, activePage }),
		[totalPages, activePage]
	);

	const goToPrevPage = useCallback(() => {
		if (activePage <= 0) return;

		setActivePage(activePage - 1);
	}, [activePage, setActivePage]);

	const goToNextPage = useCallback(() => {
		if (activePage >= totalPages - 1) return;

		setActivePage(activePage + 1);
	}, [activePage, totalPages, setActivePage]);

	const goToPage = useCallback(
		(page) => {
			setActivePage(page);
		},
		[setActivePage]
	);

	const handleDotsClick = useCallback(
		(direction) => {
			if (direction === 'left') {
				setActivePage(Math.max(activePage - DOTS_JUMP, 0));
			} else {
				setActivePage(Math.min(activePage + DOTS_JUMP, totalPages - 1));
			}
		},
		[activePage, totalPages, setActivePage]
	);

	const rangeLabel = useMemo(() => {
		if (totalCount === 0 || size === 0) return '';

		const start = activePage * size + 1;
		const end = Math.min((activePage + 1) * size, totalCount);

		return `${start} - ${end} of ${Math.floor(totalCount).toLocaleString()}`;
	}, [activePage, size, totalCount]);

	const isPrevDisabled = activePage === 0;
	const isNextDisabled = activePage >= totalPages - 1;
	const showPagination = totalCount > 0;

	return {
		paginationRange,
		rangeLabel,
		isPrevDisabled,
		isNextDisabled,
		showPagination,
		goToPrevPage,
		goToNextPage,
		goToPage,
		handleDotsClick,
	};
};
